import { Link } from "react-router-dom";
import { Layout } from "@/components/layout/Layout";
import { SEO } from "@/components/SEO";
import { Breadcrumbs } from "@/components/layout/Breadcrumbs";
import { Receipt, ArrowRight } from "lucide-react";
import { generateBreadcrumbSchema, organizationSchema, localBusinessSchema } from "@/lib/structuredData";

const tools = [
  {
    icon: Receipt,
    title: "VAT Calculator",
    description: "Add or remove 15% VAT instantly. Built for South African pricing and invoicing.",
    href: "/tools/vat-calculator",
  },
];

export default function Tools() {
  const itemListSchema = {
    "@context": "https://schema.org",
    "@type": "ItemList",
    name: "Free South African Tax & Bookkeeping Calculators",
    itemListElement: tools.map((t, i) => ({
      "@type": "ListItem",
      position: i + 1,
      name: t.title,
      url: `https://bookkeepingservices.co.za${t.href}`,
    })),
  };

  return (
    <Layout>
      <SEO
        title="Free SA Tax Calculators | VAT Calculator | Tools"
        description="Free South African tax and bookkeeping calculator. Add or remove 15% VAT instantly — built for SA invoicing and pricing in 2026."
        canonical="/tools"
        structuredData={[
          organizationSchema,
          localBusinessSchema,
          itemListSchema,
          generateBreadcrumbSchema([
            { name: "Home", url: "https://bookkeepingservices.co.za/" },
            { name: "Tools", url: "https://bookkeepingservices.co.za/tools" },
          ]),
        ]}
      />

      <Breadcrumbs items={[{ name: "Tools & Calculators" }]} />

      <section className="py-12 lg:py-20">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto text-center mb-12">
            <span className="inline-block px-4 py-1.5 rounded-full bg-primary/10 text-primary text-sm font-semibold mb-4">
              Free Tools
            </span>
            <h1 className="font-heading text-4xl md:text-5xl font-bold text-foreground mb-4">
              South African Tax & Payroll Calculators
            </h1>
            <p className="text-lg text-muted-foreground">
              Free, accurate calculators built by SAICA registered accountants. Aligned to current SARS rates and tables.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-6xl mx-auto">
            {tools.map((tool) => (
              <Link
                key={tool.href}
                to={tool.href}
                className="group bg-card border border-border rounded-2xl p-7 hover:border-primary/40 hover:shadow-lg transition-all"
              >
                <div className="w-12 h-12 rounded-xl bg-primary/10 text-primary flex items-center justify-center mb-5">
                  <tool.icon className="w-6 h-6" />
                </div>
                <h2 className="font-heading text-xl font-bold mb-2 group-hover:text-primary transition-colors">
                  {tool.title}
                </h2>
                <p className="text-muted-foreground text-sm mb-5">{tool.description}</p>
                <span className="inline-flex items-center gap-1 text-primary text-sm font-semibold">
                  Open calculator <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                </span>
              </Link>
            ))}
          </div>
        </div>
      </section>
    </Layout>
  );
}
